import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Music, MessageSquare, Settings, ArrowRight, Tag, Database, Sparkles, Play, FlaskConical } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useAuth } from '@/contexts/AuthContext';

const AdminHome = ({ t }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const adminSections = [
    {
      id: 'sounds',
      title: 'Bibliothèque Sonore',
      description: 'Gérez les sons apaisants (musique, nature, classique, etc.)',
      icon: Music,
      color: 'blue',
      route: '/admin/sounds',
      items: [
        'Upload de fichiers audio haute qualité',
        'Édition des noms FR/EN',
        'Organisation par catégorie (8 catégories)',
        'Gestion complète de la bibliothèque musicale'
      ]
    },
    {
      id: 'display-names',
      title: 'Display Names V1',
      description: 'Gérez les noms affichés (FR/EN) pour les 7 catégories canoniques V1',
      icon: Tag,
      color: 'green',
      route: '/admin/display-names',
      items: [
        'Classical, Ambient, Chill/Lofi',
        'Breathing, Nature, Sleep, Pulse Flow',
        'Édition des noms FR et EN',
        'Recherche et filtrage rapide'
      ]
    },
    {
      id: 'voice-messages',
      title: 'Messages Vocaux & SOS',
      description: 'Gérez les messages vocaux pour Radio Hearen et messages apaisants SOS Calm',
      icon: MessageSquare,
      color: 'purple',
      route: '/admin/voice-messages',
      items: [
        '📻 Messages Radio (tous les canaux)',
        '❤️ Messages SOS Calm (voix apaisantes)',
        '✏️ Création et édition de textes',
        '🎙️ Génération TTS automatique'
      ]
    },
    {
      id: 'backups',
      title: 'Backups MongoDB',
      description: 'Sauvegardez et téléchargez les données de la base MongoDB',
      icon: Database,
      color: 'orange',
      route: '/admin/backups',
      items: [
        '💾 Création de backups compressés',
        '📥 Téléchargement des archives',
        '⏱️ Expiration automatique (24h)',
        '🔒 Accès sécurisé admin uniquement'
      ]
    },
    {
  id: 'home-lab',
  title: 'Home Simulation Lab',
  description: 'Testez les suggestions J1/J2/J3/J5, visualisez les décisions moteur et les analytics',
  icon: FlaskConical,
  color: 'amber',
  route: '/admin/home-lab',
  items: [
    'Simulation J1/J2/J3/J5 en temps réel',
    'Override moment, sessions, thème',
    'Debug panel moteur de décision',
    'Analytics events log'
  ]
},
{
  id: 'native-audio-lab',
  title: 'Native Audio Lab',
  description: 'Tester le moteur audio natif iOS : PING, LOAD, PLAY, PAUSE, STOP, EQ',
  icon: FlaskConical,
  color: 'violet',
  route: '/admin/native-audio-lab',
  items: [
    'Test PING du plugin iOS',
    'LOAD / PLAY / PAUSE / STOP',
    'EQ ON / OFF',
    'Validation lockscreen'
  ]
}
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-32">
      <PageHeader title="Administration Hearen" showBack={true} />
      
      <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">
              Panneau d'Administration
            </h1>
          </div>
          <p className="text-gray-600">
            Gérez la bibliothèque sonore et les messages vocaux de Hearen
          </p>
        </div>

        {/* Admin Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {adminSections.map((section) => {
            const IconComponent = section.icon;
            
            // V1.1 UX: Unified color classes for consistent contrast
            const colorConfig = {
              blue: {
                border: 'border-blue-300 hover:border-blue-400',
                iconBg: 'bg-blue-100',
                iconText: 'text-blue-600',
                accent: 'blue'
              },
              green: {
                border: 'border-amber-300 hover:border-amber-400',
                iconBg: 'bg-amber-100',
                iconText: 'text-amber-600',
                accent: 'amber'
              },
              purple: {
                border: 'border-purple-300 hover:border-purple-400',
                iconBg: 'bg-purple-100',
                iconText: 'text-purple-600',
                accent: 'purple'
              },
              orange: {
                border: 'border-orange-300 hover:border-orange-400',
                iconBg: 'bg-orange-100',
                iconText: 'text-orange-600',
                accent: 'orange'
              },
              amber: {
                border: 'border-yellow-300 hover:border-yellow-400',
                iconBg: 'bg-yellow-100',
                iconText: 'text-yellow-600',
                accent: 'yellow'
              }
            };
            
            const colors = colorConfig[section.color] || colorConfig.blue;

            return (
              <Card 
                key={section.id}
                className={`
                  border-2 !bg-white cursor-pointer 
                  transition-all duration-200 
                  hover:shadow-lg hover:-translate-y-1 
                  ${colors.border}
                `}
                onClick={() => navigate(section.route)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {/* Icon with colored background */}
                      <div className={`p-3 rounded-xl ${colors.iconBg}`}>
                        <IconComponent className={`w-6 h-6 ${colors.iconText}`} />
                      </div>
                      <div>
                        {/* Title - high contrast, bold - forced inline styles for visibility */}
                        <h3 className="text-xl font-bold" style={{ color: 'rgba(17, 24, 39, 0.95)' }}>
                          {section.title}
                        </h3>
                        {/* Description - good contrast, medium weight */}
                        <p className="mt-1 font-medium text-sm" style={{ color: 'rgba(55, 65, 81, 0.85)' }}>
                          {section.description}
                        </p>
                      </div>
                    </div>
                    {/* Arrow - visible */}
                    <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {section.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-800">
                        <span className="text-gray-600 mt-1 font-bold">•</span>
                        <span className="font-medium">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full mt-4"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(section.route);
                    }}
                  >
                    Accéder
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Sections */}
        <div className="mt-8 space-y-4">
          {/* Important Note */}
          <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
            <div className="flex gap-3">
              <div className="text-blue-600 mt-0.5">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-blue-900 mb-1">Note importante</h3>
                <p className="text-sm text-blue-800 font-medium">
                  Les modifications apportées ici sont immédiatement disponibles pour tous les utilisateurs. 
                  Assurez-vous de vérifier vos changements avant de les publier.
                </p>
              </div>
            </div>
          </div>

          {/* Test "Discover Hearen" Journey */}
          <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 border-2 border-violet-300 rounded-lg">
            <div className="flex gap-3 items-start">
              <div className="text-violet-600 mt-0.5">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-violet-900 mb-1">🧪 Test "Discover Hearen"</h3>
                <p className="text-sm text-violet-800 font-medium mb-3">
                  Lancez le parcours de découverte pour tester l'expérience utilisateur.<br/>
                  Ce parcours inclut : voix Hera, respiration guidée et son d'ambiance Calm_031.
                </p>
                <Button 
                  onClick={() => navigate('/journey-intro')}
                  className="bg-violet-600 hover:bg-violet-700 text-white font-semibold shadow-md"
                  data-testid="launch-discover-hearen-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Lancer Discover Hearen
                </Button>
              </div>
            </div>
          </div>

          {/* SOS Calm Info */}
          <div className="p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
            <div className="flex gap-3">
              <div className="text-purple-600 mt-0.5">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-purple-900 mb-1">❤️ À propos de SOS Calm</h3>
                <p className="text-sm text-purple-800 font-medium">
                  <strong>SOS Calm combine musiques apaisantes et messages vocaux.</strong>
                </p>
                <ul className="mt-2 ml-4 text-sm text-purple-800 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    <span><strong>Musiques :</strong> Gérez-les dans "Bibliothèque Sonore" → Catégorie "SOS Calm"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600">•</span>
                    <span><strong>Messages vocaux :</strong> Gérez-les dans "Messages Vocaux & SOS" → Onglet "Messages d'Apaisement"</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHome;
